import { Component, effect, inject, input, output } from '@angular/core';
import { DxPivotGridComponent } from 'devextreme-angular';
import { DxoFieldChooserComponent, DxoLoadPanelComponent } from 'devextreme-angular/ui/nested';
import PivotGridDataSource from 'devextreme/ui/pivot_grid/data_source';
import { Measure } from '@barthauer/custom/xmt-models';
import { PivotGridCell } from 'devextreme/excel_exporter';
import { FlexModule } from 'ngx-flexible-layout';
import { CellClickEvent } from 'devextreme/ui/pivot_grid';
import { DataService } from '../../services/data.service';

@Component({
  selector: 'ubt-geods-matrix',
  templateUrl: './matrix.component.html',
  styleUrls: ['./matrix.component.scss'],
  imports: [DxPivotGridComponent, DxoFieldChooserComponent, DxoLoadPanelComponent, FlexModule],
})
export class MatrixComponent {
  private readonly dataService = inject(DataService);

  protected measures = input<Partial<Measure>[]>();
  protected selectedValues = output<Partial<Measure>>();

  private clickedCellElement: HTMLElement | null = null;

  protected pivotGridDataSource: PivotGridDataSource = new PivotGridDataSource();
  protected res: (key: string, ...value: string[]) => string = this.dataService.res;

  constructor() {
    effect(() => {
      this.initializePivotGrid();
    });
  }
  protected onCellPrepared(e: any): void {
    if (e.area === 'data' && e.cell.rowPath && e.cell.columnPath) {
      const benefit = e.cell.rowPath[0];
      const effort = e.cell.columnPath[0];
      e.cellElement.style.color = '#fff';
      e.cellElement.style.fontWeight = 'bold';
      e.cellElement.style.textAlign = 'center';
      e.cellElement.style.verticalAlign = 'middle';
      let color: string;
      if (benefit + effort >= 10) {
        color = '#30a661';
      } else if (benefit + effort >= 8) {
        color = '#80c639';
      } else if (benefit + effort >= 6) {
        color = '#f7c222';
      } else if (benefit + effort >= 4) {
        color = '#f47b25';
      } else color = '#e14747';
      e.cellElement.style.backgroundColor = color;
    }
  }
  protected styleCell(e: CellClickEvent): void {
    if (e.area === 'data') {
      if (e.cell.text === '') {
        if (this.clickedCellElement) {
          this.resetCellStyle(this.clickedCellElement);
          this.clickedCellElement = null;
        }
        return;
      }
      const benefit = e.cell?.rowPath?.[0] as number;
      const effort = e.cell?.columnPath?.[0] as number;
      const cellElement = e.cellElement as HTMLElement;
      if (this.clickedCellElement && this.clickedCellElement !== cellElement) {
        this.resetCellStyle(this.clickedCellElement);
      }
      if (this.clickedCellElement === cellElement) {
        this.resetCellStyle(cellElement);
        this.clickedCellElement = null;
        this.selectedValues.emit(null);
      } else {
        this.enlargeCell(cellElement, effort, benefit);
        this.clickedCellElement = cellElement;
        this.selectedValues.emit({ Effort: effort, Benefit: benefit });
      }
    }
  }
  private initializePivotGrid(): void {
    this.pivotGridDataSource = new PivotGridDataSource({
      fields: [
        {
          dataField: 'Effort',
          area: 'column',
          expanded: true,
          sortOrder: 'asc',
        },
        {
          dataField: 'Benefit',
          area: 'row',
          expanded: true,
          sortOrder: 'desc',
        },
        {
          dataField: 'Effort',
          area: 'data',
          summaryType: 'count',
          customizeText: (cellInfo: PivotGridCell) => {
            return cellInfo.value - 1 > 0 ? (cellInfo.value - 1).toString() : '';
          },
        },
      ],
      store: this.measures().length ? [...this.generateDummyValues(), ...this.measures()] : [],
    });
    this.pivotGridDataSource.reload();
  }
  private generateDummyValues(): Partial<Measure>[] {
    const combinations = [];
    for (let i = 1; i <= 6; i++) {
      for (let j = 1; j <= 6; j++) {
        combinations.push({ Effort: i, Benefit: j });
      }
    }
    return combinations;
  }
  private enlargeCell(element: HTMLElement, effort: number, benefit: number): void {
    element.style.position = 'relative';
    let translateX = '0%';
    if (effort === 1) translateX = '10%';
    else if (effort === 6) translateX = '-10%';
    let translateY = '0%';
    if (benefit === 1) translateY = '-10%';
    else if (effort === 6) translateY = '10%';
    element.style.transform = `translate(${translateX}, ${translateY}) scale(1.3)`;
    element.style.zIndex = '1000';
    element.style.boxShadow = '0 12px 30px rgba(0, 0, 0, 0.5)';
    element.style.filter = 'brightness(1.1)';
  }
  private resetCellStyle(element: HTMLElement): void {
    element.style.position = 'static';
    element.style.transform = 'scale(1)';
    element.style.zIndex = '1';
    element.style.boxShadow = 'none';
    element.style.filter = 'brightness(1)';
  }
}
